import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

/** Minimum allowed Dataverse synthetic-column length. */
const MIN_DATAVERSE_LENGTH = 50;

/** Maximum allowed Dataverse synthetic-column length. */
const MAX_DATAVERSE_LENGTH = 4000;

/** Backend default lengths for a new profile (mirrors the Profile entity defaults). */
export const DEFAULT_DATAVERSE_LENGTHS: Required<DataverseColumnLengthsValue> = {
  dataverseLookupLabelMaxLength: 1000,
  dataverseChoiceLabelMaxLength: 1000,
  dataverseMultiSelectMaxLength: 2000,
};

/**
 * The three configurable Dataverse synthetic-column lengths. Each is optional so an empty input
 * can mean "leave unchanged" in the edit flow.
 */
export interface DataverseColumnLengthsValue {
  dataverseLookupLabelMaxLength?: number;
  dataverseChoiceLabelMaxLength?: number;
  dataverseMultiSelectMaxLength?: number;
}

/**
 * Returns whether any provided length is outside the allowed range.
 * @param value The current length values.
 */
export function hasOutOfRangeDataverseLength(value: DataverseColumnLengthsValue): boolean {
  return [
    value.dataverseLookupLabelMaxLength,
    value.dataverseChoiceLabelMaxLength,
    value.dataverseMultiSelectMaxLength,
  ].some((v) => v !== undefined && (v < MIN_DATAVERSE_LENGTH || v > MAX_DATAVERSE_LENGTH));
}

/**
 * Props for {@link DataverseColumnLengthsFields}.
 */
interface DataverseColumnLengthsFieldsProps {
  /** Current length values. */
  value: DataverseColumnLengthsValue;
  /** Called with the next values whenever one input changes. */
  onChange: (next: DataverseColumnLengthsValue) => void;
  /** Whether the inputs are disabled (e.g. non-admin). */
  disabled?: boolean;
  /**
   * The original persisted values, used only to show a soft "reducing a length" warning in the
   * edit flow. Omit in the creation flow (no prior values to reduce from).
   */
  original?: Required<DataverseColumnLengthsValue>;
}

/**
 * The three Dataverse synthetic-column length inputs (lookup label, choice label, multi-select),
 * with range validation and a soft reduce-warning. Shared by the profile-edit dialog and the
 * creation wizard's advanced settings so the two surfaces cannot drift.
 */
export function DataverseColumnLengthsFields({
  value,
  onChange,
  disabled = false,
  original,
}: DataverseColumnLengthsFieldsProps) {
  const { t } = useLanguage();

  /** Updates one length field; an empty input clears it (leaves it unchanged in edit mode). */
  const handleChange = (key: keyof DataverseColumnLengthsValue, raw: string) => {
    onChange({ ...value, [key]: raw === '' ? undefined : Number(raw) });
  };

  const isReducing =
    original !== undefined &&
    ((value.dataverseLookupLabelMaxLength ?? original.dataverseLookupLabelMaxLength) <
      original.dataverseLookupLabelMaxLength ||
      (value.dataverseChoiceLabelMaxLength ?? original.dataverseChoiceLabelMaxLength) <
        original.dataverseChoiceLabelMaxLength ||
      (value.dataverseMultiSelectMaxLength ?? original.dataverseMultiSelectMaxLength) <
        original.dataverseMultiSelectMaxLength);

  return (
    <div className="grid gap-2">
      <Label>{t.profiles.editDialog.dataverseLengthsTitle}</Label>
      <p className="text-xs text-muted-foreground">
        {t.profiles.editDialog.dataverseLengthsDescription}
      </p>
      <div className="grid grid-cols-3 gap-2">
        <div className="grid gap-1">
          <Label htmlFor="lookupLabelLen" className="text-xs">
            {t.profiles.editDialog.lookupLabelLength}
          </Label>
          <Input
            id="lookupLabelLen"
            type="number"
            min={MIN_DATAVERSE_LENGTH}
            max={MAX_DATAVERSE_LENGTH}
            disabled={disabled}
            value={value.dataverseLookupLabelMaxLength ?? ''}
            onChange={(e) => { handleChange('dataverseLookupLabelMaxLength', e.target.value); }}
          />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="choiceLabelLen" className="text-xs">
            {t.profiles.editDialog.choiceLabelLength}
          </Label>
          <Input
            id="choiceLabelLen"
            type="number"
            min={MIN_DATAVERSE_LENGTH}
            max={MAX_DATAVERSE_LENGTH}
            disabled={disabled}
            value={value.dataverseChoiceLabelMaxLength ?? ''}
            onChange={(e) => { handleChange('dataverseChoiceLabelMaxLength', e.target.value); }}
          />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="multiSelectLen" className="text-xs">
            {t.profiles.editDialog.multiSelectLength}
          </Label>
          <Input
            id="multiSelectLen"
            type="number"
            min={MIN_DATAVERSE_LENGTH}
            max={MAX_DATAVERSE_LENGTH}
            disabled={disabled}
            value={value.dataverseMultiSelectMaxLength ?? ''}
            onChange={(e) => { handleChange('dataverseMultiSelectMaxLength', e.target.value); }}
          />
        </div>
      </div>
      {isReducing && (
        <div className="flex items-start gap-2 rounded-md bg-muted p-2 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t.profiles.editDialog.lengthReduceWarning}</span>
        </div>
      )}
    </div>
  );
}
